import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtom } from 'jotai';
import { isPossiblePhoneNumber } from 'libphonenumber-js';
import AuthCode from 'react-auth-code-input';
import { Copy, Loader2 } from 'lucide-react';

import { authenticationAtom } from '../State';
import { useAdministrationAPI } from '../AdministrationAPI';
import { useModalContext } from '../components/ModalProvider';
import { setupAuthenticatorApp, verifyAuthenticatorApp, disableMFA, updateUserCredentials } from '../UserAPI';
import Section from '../components/Section';
import { PasswordChangePanel } from '../components/PasswordChangePanel';
import PhoneInput from '../components/PhoneInput';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';

interface Properties {
}

export default function SecurityPanel(_properties: Properties) {
    const { t } = useTranslation();
    const { pushError, showConfirmationDialog } = useModalContext() as any;
    const { updateUser } = useAdministrationAPI();
    const [authentication, setAuthentication] = useAtom(authenticationAtom) as any;
    const fieldId = useId();
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [mfaDialogOpen, setMFADialogOpen] = useState(false);
    const [password, setPassword] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [isPasswordValid, setPasswordValid] = useState(false);
    const [changingPassword, setChangingPassword] = useState(false);
    const [editedMobileNumber, setEditedMobileNumber] = useState<string | undefined>();
    const [savingMobileNumber, setSavingMobileNumber] = useState(false);
    const [authAppDialogOpen, setAuthAppDialogOpen] = useState(false);
    const [authAppEnabled, setAuthAppEnabled] = useState(authentication.authenticatorAppEnabled);
    const [authAppStep, setAuthAppStep] = useState<'setup' | 'verify' | 'done'>('setup');
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [authAppSecret, setAuthAppSecret] = useState<string | null>(null);
    const [startingAuthAppSetup, setStartingAuthAppSetup] = useState(false);
    const [disablingAuthApp, setDisablingAuthApp] = useState(false);
    const [verificationCode, setVerificationCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState<string | null>(null);

    const authAppSecretId = `${fieldId}-authenticator-secret`;
    const mobileNumberId = `${fieldId}-mobile-number`;

    const openMFADialog = () => {
        setEditedMobileNumber(authentication.mobileNumber);
        setMFADialogOpen(true);
    }
    const isPhoneNumberValid = (): boolean => {
        return isPossiblePhoneNumber(editedMobileNumber || '');
    }

    const openAuthAppDialog = () => {
        setAuthAppDialogOpen(true);
        setAuthAppStep('setup');
        setQrCodeUrl(null);
        setAuthAppSecret(null);
        setVerificationCode('');
        setVerifyError(null);
    };

    async function startAuthAppSetup() {
        // Call backend to get QR code and secret
        setStartingAuthAppSetup(true);
        setVerifyError(null);
        try {
            const data = await setupAuthenticatorApp();
            setQrCodeUrl(data.qrCodeUrl);
            setAuthAppSecret(data.secret);
            setAuthAppStep('verify');
        } catch (e) {
            setVerifyError(t('settingsPanel.securityPanel.setupFailed'));
        } finally {
            setStartingAuthAppSetup(false);
        }
    }

    async function verifyAuthAppCode(code: string) {
        setVerifying(true);
        setVerifyError(null);
        try {
            const data = await verifyAuthenticatorApp(code, authAppSecret!);
            if (data.success) {
                setAuthAppEnabled(true);
                setAuthAppStep('done');
            } else {
                setVerifyError(t('settingsPanel.securityPanel.invalidCode'));
            }
        } catch (e) {
            setVerifyError(t('settingsPanel.securityPanel.verificationFailed'));
        }
        setVerifying(false);
    }

    const disableAuthApp = async () => {
        setDisablingAuthApp(true);
        try {
            await disableMFA();
            setAuthAppEnabled(false);
            setAuthentication({ ...authentication, authenticatorAppEnabled: false });
            toast.success(t('settingsPanel.securityPanel.authenticatorAppDisabled'));
        } catch (e) {
            pushError(t('settingsPanel.securityPanel.disableFailed'));
        } finally {
            setDisablingAuthApp(false);
        }
    };

    const copyAuthAppSecret = async () => {
        if (!authAppSecret) {
            return;
        }

        try {
            await navigator.clipboard.writeText(authAppSecret);
            toast.success(t('global.copied'));
        } catch (error) {
            pushError(error);
        }
    };

    const changePassword = async () => {
        setChangingPassword(true);
        try {
            await updateUserCredentials(
                authentication.customer,
                authentication._id,
                currentPassword,
                password);
            setAuthentication({ ...authentication }); // Optionally refresh user info
            setPasswordDialogOpen(false);
            toast.success(t('settingsPanel.securityPanel.passwordChanged'));
        } catch (error: any) {
            pushError(error?.message || error);
        } finally {
            setChangingPassword(false);
        }
    };

    const saveMobileNumber = async () => {
        setSavingMobileNumber(true);
        try {
            const newAuthentication = { ...authentication, mobileNumber: editedMobileNumber };

            await updateUser(newAuthentication.customer, newAuthentication);

            setAuthentication(newAuthentication);
            setMFADialogOpen(false);
            toast.success(t('settingsPanel.securityPanel.mobileNumberSaved'));
        } catch (error) {
            pushError(error);
        } finally {
            setSavingMobileNumber(false);
        }
    };

    return <>
        <div className="flex w-full min-w-0 max-w-2xl flex-col gap-6">
            <Section title={t('settingsPanel.securityPanel.password')}>
                <div className="flex flex-wrap justify-end gap-2">
                    <Button data-guide-id="settings.securityProfile.changePassword" variant="outline" onClick={() => setPasswordDialogOpen(true)}>
                        {t('global.change')}
                    </Button>
                </div>
            </Section>
            <Section title={t('settingsPanel.securityPanel.mfa')}>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <span className="text-sm font-semibold">{t('settingsPanel.securityPanel.mobileNumber')}</span>
                        <span className="min-w-0 break-words text-sm">{authentication.mobileNumber}</span>
                        <div className="ml-auto flex flex-wrap items-center gap-2">
                            <Button data-guide-id="settings.securityProfile.mfa" variant="outline" onClick={openMFADialog}>
                                {t('global.change')}
                            </Button>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                        <span className="text-sm font-semibold">
                            {t('settingsPanel.securityPanel.authenticatorApp')}
                        </span>
                        <span className={authAppEnabled ? 'text-sm font-medium text-success' : 'text-sm text-muted-foreground'}>
                            {authAppEnabled ? t('settingsPanel.securityPanel.enabled') : t('settingsPanel.securityPanel.notEnabled')}
                        </span>
                        <div className="ml-auto flex flex-wrap items-center gap-2">
                            {authAppEnabled
                                ?
                                <Button
                                    data-guide-id="settings.securityProfile.authApp"
                                    variant="outline"
                                    disabled={disablingAuthApp}
                                    onClick={() => {
                                        showConfirmationDialog(
                                            t('settingsPanel.securityPanel.disableAuthenticatorApp'),
                                            t('settingsPanel.securityPanel.disableAuthenticatorAppConfirm'),
                                            disableAuthApp
                                        );
                                    }}
                                >
                                    {disablingAuthApp && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                                    {disablingAuthApp ? t('settingsPanel.securityPanel.disabling') : t('global.disable')}
                                </Button> :
                                <Button data-guide-id="settings.securityProfile.authApp" variant="outline" onClick={openAuthAppDialog}>
                                    {t('global.enable')}
                                </Button>
                            }
                        </div>
                    </div>
                </div>
            </Section>
        </div>
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.securityPanel.changePassword')}</DialogTitle>
                </DialogHeader>
                <PasswordChangePanel
                    setCurrentPassword={setCurrentPassword}
                    setPassword={setPassword}
                    setPasswordValid={setPasswordValid}
                />
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                        {t('global.cancel')}
                    </Button>
                    <Button
                        disabled={!isPasswordValid || !currentPassword || changingPassword}
                        onClick={changePassword}
                    >
                        {changingPassword && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                        {changingPassword ? t('settingsPanel.securityPanel.changingPassword') : t('global.change')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <Dialog open={mfaDialogOpen} onOpenChange={setMFADialogOpen}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.securityPanel.changeMobileNumber')}</DialogTitle>
                </DialogHeader>
                <div className="flex w-full min-w-0 max-w-2xl flex-col gap-1.5">
                    <Label htmlFor={mobileNumberId}>
                        {t('settingsPanel.securityPanel.mobileNumber')}
                        <span aria-hidden="true" className="ml-0.5 text-destructive">*</span>
                    </Label>
                    <PhoneInput
                        id={mobileNumberId}
                        value={editedMobileNumber}
                        onChange={(value: string) => setEditedMobileNumber(value)}
                        onValidationChange={() => {}}
                        required
                    />
                </div>
                <DialogFooter className="gap-2 pt-4 sm:gap-0">
                    <Button variant="outline" onClick={() => setMFADialogOpen(false)}>
                        {t('global.cancel')}
                    </Button>
                    <Button
                        disabled={!isPhoneNumberValid() || savingMobileNumber}
                        onClick={saveMobileNumber}
                    >
                        {savingMobileNumber && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                        {savingMobileNumber ? t('settingsPanel.securityPanel.savingMobileNumber') : t('global.change')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <Dialog open={authAppDialogOpen} onOpenChange={setAuthAppDialogOpen}>
            <DialogContent
                className="max-w-[calc(100vw-2rem)] sm:max-w-[600px]"
                onInteractOutside={(event) => event.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle>{t('settingsPanel.securityPanel.setupAuthenticatorApp')}</DialogTitle>
                </DialogHeader>
                {authAppStep === 'setup' && (
                    <div className="flex flex-col gap-4">
                        <p className="text-sm">{t('settingsPanel.securityPanel.authenticatorAppSetupInstructions')}</p>
                        {verifyError && (
                            <p role="alert" className="text-sm text-destructive">{verifyError}</p>
                        )}
                        <div className="flex flex-wrap gap-2">
                            <Button onClick={startAuthAppSetup} disabled={startingAuthAppSetup}>
                                {startingAuthAppSetup && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                                {startingAuthAppSetup
                                    ? t('settingsPanel.securityPanel.loadingQrCode')
                                    : t('settingsPanel.securityPanel.showQr')}
                            </Button>
                        </div>
                    </div>
                )}
                {authAppStep === 'verify' && (
                    <>
                        <div className="flex flex-col gap-4">
                            <p className="text-sm">{t('settingsPanel.securityPanel.scanQr')}</p>
                            {qrCodeUrl && (
                                <div className="flex justify-center">
                                    <img
                                        src={qrCodeUrl}
                                        alt={t('settingsPanel.securityPanel.qrCodeAlt')}
                                        className="h-[180px] w-[180px] max-w-full"
                                        width={180}
                                        height={180}
                                    />
                                </div>
                            )}
                            {authAppSecret && (
                                <div className="flex min-w-0 flex-col gap-1.5">
                                    <Label htmlFor={authAppSecretId}>
                                        {t('settingsPanel.securityPanel.authenticatorKeyForRecovery')}
                                    </Label>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Input
                                            id={authAppSecretId}
                                            readOnly
                                            value={authAppSecret}
                                            className="min-w-0 flex-1 bg-muted font-mono"
                                        />
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="shrink-0"
                                            aria-label={t('global.copy')}
                                            title={t('global.copy')}
                                            onClick={copyAuthAppSecret}
                                        >
                                            <Copy aria-hidden="true" className="h-4 w-4" />
                                        </Button>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {t('settingsPanel.securityPanel.authenticatorKeyDescription')}
                                    </p>
                                </div>
                            )}
                            <div className="flex flex-col gap-2">
                                <p className="text-sm text-muted-foreground">
                                    {t('settingsPanel.securityPanel.enterAuthenticatorCodeDescription')}
                                </p>
                                <AuthCode
                                    onChange={(val: string) => {
                                        setVerificationCode(val);
                                    }}
                                    allowedCharacters="numeric"
                                    length={6}
                                    ariaLabel={t('settingsPanel.securityPanel.enterAuthenticatorCode')}
                                    containerClassName="flex flex-wrap justify-center gap-2"
                                    inputClassName="h-11 w-10 rounded-md border border-input bg-background text-center text-lg text-foreground shadow-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                                    autoFocus
                                />
                            </div>
                            {verifyError && (
                                <p role="alert" className="text-sm text-destructive">{verifyError}</p>
                            )}
                        </div>
                        <DialogFooter className="gap-2 pt-4 sm:gap-0">
                            <Button variant="outline" onClick={() => setAuthAppDialogOpen(false)}>
                                {t('global.cancel')}
                            </Button>
                            <Button
                                onClick={() => verifyAuthAppCode(verificationCode)}
                                disabled={verificationCode.length !== 6 || verifying}
                            >
                                {verifying && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}
                                {verifying
                                    ? t('settingsPanel.securityPanel.verifying')
                                    : t('settingsPanel.securityPanel.verify')}
                            </Button>
                        </DialogFooter>
                    </>
                )}
                {authAppStep === 'done' && (
                    <>
                        <p className="text-sm">{t('settingsPanel.securityPanel.authenticatorAppEnabled')}</p>
                        <DialogFooter className="gap-2 pt-4 sm:gap-0">
                            <Button onClick={() => setAuthAppDialogOpen(false)}>
                                {t('global.done')}
                            </Button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    </>;
}
